<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ApplyInvoiceModal from 'dashboard/components-next/ApplyInvoiceModal.vue';
import EnterpriseInfoModal from 'dashboard/components-next/EnterpriseInfoModal.vue';
import SeatOrdersAPI from 'dashboard/api/seatOrders';

const { t } = useI18n();

const orders = ref([]);
const isLoading = ref(true);
const error = ref(null);

// 发票相关状态
const showInvoiceModal = ref(false);
const showEnterpriseModal = ref(false);
const selectedOrder = ref(null);

// 筛选参数
const filterStatus = ref('');
const filterStartDate = ref('');
const filterEndDate = ref('');

// 分页参数
const currentPage = ref(1);
const perPage = ref(20);
const totalPages = ref(1);
const totalCount = ref(0);

const fetchOrders = async () => {
  isLoading.value = true;
  error.value = null;
  try {
    const params = {
      page: currentPage.value,
      per_page: perPage.value,
    };

    if (filterStatus.value) {
      params.status = filterStatus.value;
    }
    if (filterStartDate.value) {
      params.start_date = filterStartDate.value;
    }
    if (filterEndDate.value) {
      params.end_date = filterEndDate.value;
    }

    const response = await SeatOrdersAPI.getOrders(params);
    orders.value = response.data?.data || [];
    const meta = response.data?.meta || {};
    totalPages.value = meta.total_pages || 1;
    totalCount.value = meta.total_count || 0;
  } catch (err) {
    error.value = t('BILLING_INVOICES.FETCH_ERROR');
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await fetchOrders();
});

// 发票申请
const openInvoiceModal = order => {
  selectedOrder.value = order;
  showInvoiceModal.value = true;
};

const handleInvoiceSuccess = () => {
  fetchOrders();
};

// 企业信息管理
const openEnterpriseModal = () => {
  showInvoiceModal.value = false;
  showEnterpriseModal.value = true;
};

const handleEnterpriseSuccess = () => {
  showEnterpriseModal.value = false;
  showInvoiceModal.value = true;
};

// 判断是否可以申请发票
const canApplyInvoice = order => {
  return (
    order.status === 'paid' &&
    (!order.invoice_status || order.invoice_status === 'not_applied')
  );
};

// 获取发票状态文本
const getInvoiceStatusText = order => {
  if (!order.invoice_status || order.invoice_status === 'not_applied') {
    return '';
  }
  return t(
    `BILLING_INVOICES.INVOICE_STATUS.${order.invoice_status.toUpperCase()}`
  );
};

// 获取发票状态样式
const getInvoiceStatusClass = order => {
  const statusClasses = {
    applied: 'bg-blue-100 text-blue-700',
    invoiced: 'bg-green-100 text-green-700',
  };
  return statusClasses[order.invoice_status] || 'bg-gray-100 text-gray-600';
};

// 监听筛选条件变化，重置到第一页并重新获取数据
watch([filterStatus, filterStartDate, filterEndDate], () => {
  currentPage.value = 1;
  fetchOrders();
});

const clearFilters = () => {
  filterStatus.value = '';
  filterStartDate.value = '';
  filterEndDate.value = '';
};

const goToPage = page => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchOrders();
  }
};

const formatPrice = cents => {
  return (cents / 100).toFixed(2);
};

const formatDate = dateString => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusClass = status => {
  const statusClasses = {
    paid: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    cancelled: 'bg-gray-100 text-gray-600',
  };
  return statusClasses[status] || 'bg-gray-100 text-gray-600';
};

const getStatusText = status => {
  return t(`BILLING_INVOICES.STATUS.${status.toUpperCase()}`, status);
};

// 获取产品名称（支持套餐详细类型）
const getProductName = order => {
  if (order.order_type === 'plan') {
    // 根据 plan_type 显示具体的套餐名称
    if (order.plan_type === 'standard_monthly') {
      return t('BILLING_INVOICES.PRODUCT_NAME_PLAN_STANDARD_MONTHLY');
    }
    if (order.plan_type === 'standard_yearly') {
      return t('BILLING_INVOICES.PRODUCT_NAME_PLAN_STANDARD_YEARLY');
    }
    if (order.plan_type === 'pro_monthly') {
      return t('BILLING_INVOICES.PRODUCT_NAME_PLAN_PRO_MONTHLY');
    }
    if (order.plan_type === 'pro_yearly') {
      return t('BILLING_INVOICES.PRODUCT_NAME_PLAN_PRO_YEARLY');
    }
    // 匹配不上显示"未知"
    return t('BILLING_INVOICES.PRODUCT_NAME_UNKNOWN');
  }
  if (order.order_type === 'token') {
    return t('BILLING_INVOICES.PRODUCT_NAME_TOKEN');
  }
  if (order.order_type === 'seat') {
    return t('BILLING_INVOICES.PRODUCT_NAME');
  }
  // 完全未知的订单类型
  if (order.order_type === 'storage') {
    return t('BILLING_INVOICES.PRODUCT_NAME_STORAGE');
  }
  return t('BILLING_INVOICES.PRODUCT_NAME_UNKNOWN');
};

const hasOrders = computed(() => orders.value.length > 0);

const hasFilters = computed(() => {
  return filterStatus.value || filterStartDate.value || filterEndDate.value;
});

const paginationText = computed(() => {
  const start = (currentPage.value - 1) * perPage.value + 1;
  const end = Math.min(currentPage.value * perPage.value, totalCount.value);
  return `${start}-${end} / ${totalCount.value}`;
});

const getPageNumbers = computed(() => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= 7) {
    for (let i = 1; i <= total; i += 1) {
      pages.push(i);
    }
  } else if (current <= 4) {
    for (let i = 1; i <= 5; i += 1) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(total);
  } else if (current >= total - 3) {
    pages.push(1);
    pages.push('...');
    for (let i = total - 4; i <= total; i += 1) {
      pages.push(i);
    }
  } else {
    pages.push(1);
    pages.push('...');
    for (let i = current - 1; i <= current + 1; i += 1) {
      pages.push(i);
    }
    pages.push('...');
    pages.push(total);
  }

  return pages;
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('BILLING_INVOICES.LOADING')"
    :no-records-found="!hasOrders && !isLoading && !hasFilters"
    :no-records-message="$t('BILLING_INVOICES.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('BILLING_INVOICES.HEADER')"
        :description="$t('BILLING_INVOICES.DESCRIPTION')"
        feature-name="billing-invoices"
      >
        <template #actions>
          <Button
            icon="i-lucide-refresh-cw"
            :label="$t('BILLING_INVOICES.REFRESH')"
            :is-loading="isLoading"
            @click="fetchOrders"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div v-if="error" class="p-4 mb-4 text-red-600 bg-red-50 rounded-lg">
        {{ error }}
      </div>

      <div class="grid gap-6">
        <!-- 筛选区域 -->
        <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap h-[34px] leading-[34px]">{{ $t('BILLING_INVOICES.FILTER.STATUS') }}</label>
              <select
                v-model="filterStatus"
                style="margin-bottom: 0"
                class="px-3 py-2 border border-n-weak rounded-lg text-sm h-[34px] focus:outline-none focus:ring-2 focus:ring-primary"
>>>>>>> feature_ai
              >
                <option value="">{{ $t('BILLING_INVOICES.FILTER.ALL') }}</option>
                <option value="paid">{{ $t('BILLING_INVOICES.STATUS.PAID') }}</option>
                <option value="pending">{{ $t('BILLING_INVOICES.STATUS.PENDING') }}</option>
                <option value="cancelled">{{ $t('BILLING_INVOICES.STATUS.CANCELLED') }}</option>
              </select>
            </div>
            <div class="h-5 w-px bg-n-weak" />
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap h-[34px] leading-[34px]">{{ $t('BILLING_INVOICES.FILTER.START_DATE') }}</label>
              <input
                v-model="filterStartDate"
                type="date"
                style="margin-bottom: 0"
                class="px-3 py-2 border border-n-weak rounded-lg text-sm h-[34px] focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-n-slate-12 whitespace-nowrap h-[34px] leading-[34px]">{{ $t('BILLING_INVOICES.FILTER.END_DATE') }}</label>
              <input
                v-model="filterEndDate"
                type="date"
                style="margin-bottom: 0"
                class="px-3 py-2 border border-n-weak rounded-lg text-sm h-[34px] focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <Button variant="ghost" :label="$t('BILLING_INVOICES.FILTER.CLEAR')" :disabled="!hasFilters" @click="clearFilters" />
          </div>
        </div>

        <!-- 列表区域 -->
        <div class="bg-n-solid-2 rounded-xl border border-n-weak p-6">
          <div v-if="!hasOrders && hasFilters" class="py-12 text-center text-n-slate-11">
            <div class="flex flex-col items-center gap-2">
              <span>{{ $t('BILLING_INVOICES.NO_MATCH') }}</span>
              <Button variant="ghost" :label="$t('BILLING_INVOICES.FILTER.CLEAR')" @click="clearFilters" />
            </div>
          </div>
          <template v-else>
            <table class="w-full divide-y divide-n-weak">
              <thead>
                <tr>
                  <th class="py-3 text-left text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.ORDER_NO') }}
                  </th>
                  <th class="py-3 text-left text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.PRODUCT') }}
                  </th>
                  <th class="py-3 text-center text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.QUANTITY') }}
                  </th>
                  <th class="py-3 text-right text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.AMOUNT') }}
                  </th>
                  <th class="py-3 text-center text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.STATUS') }}
                  </th>
                  <th class="py-3 text-center text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.INVOICE_STATUS') }}
                  </th>
                  <th class="py-3 text-right text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.CREATED_AT') }}
                  </th>
                  <th class="py-3 text-center text-sm font-semibold text-n-slate-11">
                    {{ $t('BILLING_INVOICES.TABLE.ACTION') }}
                  </th>
                </tr>
              </thead>
              <tbody class="divide-y divide-n-weak">
                <tr v-for="order in orders" :key="order.id" class="text-n-slate-12">
                  <td class="py-4 text-sm">
                    <span class="font-mono">{{ order.order_no }}</span>
                  </td>
                  <td class="py-4 text-sm">
                    {{ getProductName(order) }}
                  </td>
                  <td class="py-4 text-sm text-center">
                    {{ order.quantity }}
                  </td>
                  <td class="py-4 text-sm text-right font-medium">
                    {{ $t('BILLING_INVOICES.CURRENCY')
                    }}{{ formatPrice(order.total_price) }}
                  </td>
                  <td class="py-4 text-center">
                    <span
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                      :class="getStatusClass(order.status)"
                    >
                      {{ getStatusText(order.status) }}
                    </span>
                  </td>
                  <td class="py-4 text-center">
                    <span
                      v-if="getInvoiceStatusText(order)"
                      class="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                      :class="getInvoiceStatusClass(order)"
                    >
                      {{ getInvoiceStatusText(order) }}
                    </span>
                    <span v-else class="text-n-slate-8 text-xs">--</span>
                  </td>
                  <td class="py-4 text-sm text-right text-n-slate-11">
                    {{ formatDate(order.created_at) }}
                  </td>
                  <td class="py-4 text-center">
                    <Button
                      v-if="canApplyInvoice(order)"
                      size="small"
                      :label="$t('BILLING_INVOICES.TABLE.APPLY_INVOICE')"
                      @click="openInvoiceModal(order)"
                    />
                    <span v-else class="text-n-slate-8 text-xs">
                      {{ getInvoiceStatusText(order) || '--' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- 分页 -->
            <div v-if="hasOrders" class="flex items-center justify-between mt-4 pt-4 border-t border-n-weak">
              <span class="text-sm text-n-slate-8">{{ paginationText }}</span>
              <div class="flex items-center gap-2">
                <button
                  class="px-3 py-1.5 text-sm rounded-lg border border-n-weak hover:bg-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="currentPage <= 1"
                  @click="goToPage(currentPage - 1)"
                >
                  {{ $t('BILLING_INVOICES.PAGINATION.PREV') }}
                </button>
                <button
                  v-for="page in getPageNumbers"
                  :key="page"
                  class="w-8 h-8 text-sm rounded-lg transition-colors"
                  :class="page === currentPage ? 'bg-n-solid-blue text-white' : 'border border-n-weak hover:bg-n-weak'"
                  :disabled="page === '...'"
                  @click="page !== '...' && goToPage(page)"
                >
                  {{ page }}
                </button>
                <button
                  class="px-3 py-1.5 text-sm rounded-lg border border-n-weak hover:bg-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  :disabled="currentPage >= totalPages"
                  @click="goToPage(currentPage + 1)"
                >
                  {{ $t('BILLING_INVOICES.PAGINATION.NEXT') }}
                </button>
              </div>
            </div>
          </template>
        </div>
      </div>
    </template>

    <!-- 发票申请弹窗 -->
    <ApplyInvoiceModal
      :show="showInvoiceModal"
      :order="selectedOrder"
      @close="showInvoiceModal = false"
      @success="handleInvoiceSuccess"
      @open-enterprise-modal="openEnterpriseModal"
    />

    <!-- 企业信息弹窗 -->
    <EnterpriseInfoModal
      :show="showEnterpriseModal"
      @close="showEnterpriseModal = false"
      @success="handleEnterpriseSuccess"
    />
  </SettingsLayout>
</template>
